/**
 * SearchBar — Unified search component with navigation.
 *
 * Features:
 *  - Search input with icon
 *  - Clear button when query is non-empty
 *  - Result count display (e.g., "3 of 12 results")
 *  - Prev/Next navigation buttons
 *  - Keyboard shortcuts: Enter = next, Shift+Enter = prev, Escape = clear
 *  - Compact variant for tight toolbar layouts
 */

import { ChevronDown,ChevronUp, Search, X } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/*  Props                                                                      */
/* -------------------------------------------------------------------------- */

export interface SearchBarProps {
  /** Current search query. */
  query: string;
  /** Callback when the query changes. */
  onQueryChange: (query: string) => void;
  /** Total number of matching results (optional). */
  resultCount?: number;
  /** Current result index (0-based, optional). */
  currentIndex?: number;
  /** Callback to navigate to next result (optional). */
  onNext?: () => void;
  /** Callback to navigate to previous result (optional). */
  onPrev?: () => void;
  /** Placeholder text. */
  placeholder?: string;
  /** Compact mode for tight toolbar layouts. */
  compact?: boolean;
  /** Additional CSS classes for the wrapper. */
  className?: string;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(({
  query,
  onQueryChange,
  resultCount,
  currentIndex,
  onNext,
  onPrev,
  placeholder = 'Search…',
  compact = false,
  className,
}, forwardedRef) => {
  const hasQuery = query.trim().length > 0;
  const hasResults = resultCount !== undefined && resultCount > 0;
  const showNavigation = hasQuery && hasResults && onNext && onPrev;

  // Merge forwarded ref with internal usage
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onQueryChange('');
    } else if (e.key === 'Enter' && showNavigation) {
      e.preventDefault();
      if (e.shiftKey) {
        onPrev();
      } else {
        onNext();
      }
    }
  };

  return (
    <div className={cn(compact ? 'flex items-center gap-1' : 'px-3 py-2', className)}>
      <div className={cn('relative flex-1 min-w-0', !compact && 'flex items-center gap-1')}>
        <Search
          className={cn(
            'absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none',
            compact ? 'left-2 h-3 w-3' : 'left-2.5 h-3.5 w-3.5',
          )}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            'w-full rounded border border-input bg-background',
            'placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-1 focus:ring-ring',
            compact
              ? 'pl-7 pr-6 py-0.5 bg-muted/50 border-border text-[11px] h-5'
              : 'flex-1 rounded-md pl-8 py-1.5 text-sm',
            !compact && (showNavigation ? 'pr-32' : hasQuery ? 'pr-8' : 'pr-2'),
          )}
        />
        {hasQuery && (
          <button
            onClick={() => onQueryChange('')}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground',
              compact ? 'right-1.5 text-[10px]' : 'right-2',
            )}
            aria-label="Clear search"
          >
            {compact ? '✕' : <X className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>

      {showNavigation && (
        <div className={cn('flex items-center shrink-0', compact ? 'gap-0.5' : 'gap-1')}>
          <span
            className={cn(
              'text-muted-foreground tabular-nums whitespace-nowrap',
              compact ? 'text-[10px]' : 'text-xs',
            )}
          >
            {(currentIndex ?? 0) + 1}/{resultCount}
          </span>
          <button
            type="button"
            onClick={onPrev}
            className={cn(
              'inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground',
              compact
                ? 'h-5 w-5 hover:bg-muted'
                : 'p-0.5 hover:bg-accent hover:text-accent-foreground transition-colors',
            )}
            title="Previous match (Shift+Enter)"
            aria-label="Previous result"
          >
            <ChevronUp className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
          </button>
          <button
            type="button"
            onClick={onNext}
            className={cn(
              'inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground',
              compact
                ? 'h-5 w-5 hover:bg-muted'
                : 'p-0.5 hover:bg-accent hover:text-accent-foreground transition-colors',
            )}
            title="Next match (Enter)"
            aria-label="Next result"
          >
            <ChevronDown className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
          </button>
        </div>
      )}
    </div>
  );
});

SearchBar.displayName = 'SearchBar';
